import {CMP} from "./cmp";
import {log} from "../util/log";
import {UserSelection} from "../user-selection";
import {VendorList} from "../model/vendor-list";
import {TCString} from "../tcstring/t-c-string";
import {AddtlConsent} from "../addtl-consent/addtl-consent";
import cmpStatus from "../cmp-status";
import {EventManager} from "../api/event-manager";
import {CustomConsent} from "../custom-consent/custom-consent";
import Metadata from "../metadata";

export class CMPFull extends CMP {
    /* STATIC */

    /* INSTANCE */
    public constructor(consentAcquired: boolean, tcString?: TCString, addtlConsent?: AddtlConsent, customConsent?: CustomConsent) {
        super();
        this.consentAcquired = consentAcquired;

        if (consentAcquired) {
            this.tcString = tcString;
            this.addtlConsent = addtlConsent;
            this.customConsent = customConsent;
        }

        this.vendorList = new VendorList();
        this.userSelection = new UserSelection();
        log.debug('CMP Full instance created');
    }

    public async load(): Promise<void> {
        log.debug('Loading CMP Full instance...');

        await this.vendorList?.load();

        /*
         * Tenemos que esperar a cargar la VendorList para poder
         * construir la TCString y el AddtlConsent ya que utilizan
         * datos de la VendorList.
         *
         * Esto sólo ocurre cuando el consentimiento no ha sido adquirido.
         */
        if (!this.consentAcquired) {
            this.tcString = TCString.buildProxy();
            this.addtlConsent = AddtlConsent.buildProxy();
            this.customConsent = CustomConsent.buildProxy();
        }

        const userSelection: UserSelection|undefined = this.userSelection;
        const vendorList: VendorList|undefined = this.vendorList;
        const tcString: TCString|undefined = this.tcString;
        const addtlConsent: AddtlConsent|undefined = this.addtlConsent;
        const customConsent: CustomConsent|undefined = this.customConsent;

        if (userSelection && vendorList && tcString && addtlConsent) {
            userSelection.load(vendorList, tcString, addtlConsent, customConsent);
        }
    }

    public override async unproxy(): Promise<CMP> {
        return this;
    }

    public override acceptAll() {
        // Creamos nueva TCString
        this.tcString = TCString.buildProxy();

        // Aceptamos toda la selección del usuario
        this.userSelection?.acceptAll();

        // Actualizamos los consents
        this.updateConsents();

        super.acceptAll();
    }

    private updateConsents(): void {
        // Actualizar y guardar TcString
        const purposesConsent: any = {};
        const purposesLITransparency: any = {};
        const acceptedSpecialFeatures: any = {};
        const acceptedVendors: any = {};
        const acceptedVendorsLI: any = {};

        this.userSelection?.acceptedPurposes().forEach(purposeId => purposesConsent[purposeId] = true);
        this.userSelection?.rejectedPurposes().forEach(purposeId => purposesConsent[purposeId] = false);
        this.userSelection?.acceptedPurposesLI().forEach(purposeId => purposesLITransparency[purposeId] = true);
        this.userSelection?.rejectedPurposesLI().forEach(purposeId => purposesLITransparency[purposeId] = false);
        this.userSelection?.acceptedSpecialFeatures().forEach((featureId: number) => {acceptedSpecialFeatures[featureId] = true;});
        this.userSelection?.rejectedSpecialFeatures().forEach((featureId: number) => {acceptedSpecialFeatures[featureId] = false;});
        this.userSelection?.acceptedVendorsIds().forEach((vendorId: number) => {acceptedVendors[vendorId] = true;});
        this.userSelection?.rejectedVendorsIds().forEach((vendorId: number) => {acceptedVendors[vendorId] = false;});
        this.userSelection?.acceptedVendorsLIIds().forEach((vendorId: number) => {acceptedVendorsLI[vendorId] = true;});
        this.userSelection?.rejectedVendorsLIIds().forEach((vendorId: number) => {acceptedVendorsLI[vendorId] = false;});

        // Actualiza la TCString
        this.tcString?.update({
            acceptedVendorsLIIds: this.userSelection?.acceptedVendorsLIIds()??[],
            maxVendorLIId: this.userSelection?.maxVendorLIAccepted()??0,
            maxVendorId: this.userSelection?.maxVendorAccepted()??0,
            acceptedVendorsIds: this.userSelection?.acceptedVendorsIds()??[],
            purposesConsent: purposesConsent,
            purposesLITransparency: purposesLITransparency,
            specialFeatureOptIns: acceptedSpecialFeatures,
            vendors: acceptedVendors,
            vendorsLI: acceptedVendorsLI
        });

        // Actualizar la addtlConsent
        this.addtlConsent?.update(
            this.userSelection?.acceptedAdditionalVendorsIds()??[],
            this.userSelection?.rejectedAdditionalVendorsIds()??[]
        );

        // Actualizar la customConsent
        this.customConsent?.update(this.userSelection?.acceptedCustomVendorsIds()??[]);

        // Actualizamos metadata
        Metadata().acceptanceLevel = this.determineAcceptanceLevel();
    }

    public saveAndAcceptConfiguration() : void {
        // Creamos nueva TCString
        this.tcString = TCString.buildProxy();

        // Actualizamos los consents
        this.updateConsents();

        // Guardamos los consentimientos
        this.persistConsent();

        // Notificamos al estado
        cmpStatus.userActionCompleted();
        cmpStatus.hideUI();

        this.consentAcquired = true;

        // Lanzamos evento de confirmación de consentimiento
        EventManager.trigger(EventManager.EVENT_CMP_SUBMIT);
    }
}
